/**
 * Ein Arbeitszeitmodell bearbeiten (AP-22-B, AP-36 Fall 1).
 *
 * Ohne Knopf: Jedes Feld speichert beim Verlassen, Auswahl sofort. Zwei Gruppen,
 * weil ihre Felder nur zusammen gültig sind — Zyklus-Länge und Anker-Montag,
 * Eintritt und Austritt. Das Tages-Soll ist eine Liste und geht als Ganzes, sobald
 * der Fokus das Raster verlässt.
 *
 * Die Zyklus-Länge setzt das Soll als **Folgefeld** mit: Aus zwei Wochen wird eins,
 * dann gehen `cycleWeeks` und das gekürzte Soll im selben PATCH. Der Server weist
 * eine Länge ab, zu der das Soll nicht passt — sonst entstünden stille 0-Stunden-Tage.
 *
 * AP-22 Teil 7: Die Wochenstunden sind kein eigenes Feld mehr — sie folgen dem Tages-Soll
 * bzw. dem Stundenrahmen, der Server rechnet sie. Ein Wechsel ab einem Stichtag entsteht
 * über den Knopf „Wechsel" als neues Modell; das alte bleibt für die Zeit davor.
 */

import { useCallback, useEffect, useMemo, useState, type FocusEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, Trash2 } from 'lucide-react';
import { Button } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { bestaetige } from '@/lib/dialoge';
import { pageWide } from '@/lib/form-styles';
import {
  Abschnitt,
  FeldStatus,
  FeldZeile,
  Feldgruppe,
  feldKlasse,
  leseZahl,
  mitStand,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
  type Status,
} from '@/lib/speichern';
import { deleteWorkSchedule, fetchCollectiveAgreements, fetchPayrollEmployees, fetchWorkSchedule } from '../api/payroll.api';
import { formatIsoDate, weekdayName } from '../lib/dates';
import { Hilfe } from '../components/Hilfe';
import { WorkScheduleChangeDialog } from '../components/WorkScheduleChangeDialog';
import type { CollectiveAgreement, WorkSchedule, WorkScheduleDay, WorkScheduleFrame } from '../types/payroll.types';

type Stelle = Speicherstelle<unknown>;

/** Soll an eine neue Zyklus-Länge anpassen: vorhandene Wochen bleiben, neue kommen leer dazu. */
function sollFuer(tage: WorkScheduleDay[], wochen: number): WorkScheduleDay[] {
  const ergebnis: WorkScheduleDay[] = [];
  for (let week = 1; week <= wochen; week++) {
    for (let weekday = 1; weekday <= 7; weekday++) {
      const vorhanden = tage.find((d) => d.week === week && d.weekday === weekday);
      ergebnis.push({ week, weekday, targetHours: vorhanden?.targetHours ?? 0 });
    }
  }
  return ergebnis;
}

function istMontag(iso: string): boolean {
  return new Date(`${iso}T00:00:00.000Z`).getUTCDay() === 1;
}

function stunden(n: number): string {
  return n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
}

export default function WorkScheduleDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const gesperrt = !erfuelltRecht('payroll.work_schedule.manage', authUser?.permissions);

  const [modell, setModell] = useState<WorkSchedule | null>(null);
  const [mitarbeiter, setMitarbeiter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [wechsel, setWechsel] = useState(false);

  useEffect(() => {
    let aktiv = true;
    setModell(null);
    fetchWorkSchedule(id)
      .then((w) => aktiv && setModell(w))
      .catch(() => aktiv && setError('Das Arbeitszeitmodell konnte nicht geladen werden.'));
    return () => {
      aktiv = false;
    };
  }, [id]);

  // Name getrennt laden — ein Fehler hier darf die Maske nicht blockieren.
  useEffect(() => {
    if (!modell) return;
    fetchPayrollEmployees()
      .then((liste) => {
        const u = liste.find((x) => x.id === modell.userId);
        if (u) setMitarbeiter([u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email || '—');
      })
      .catch(() => {
        /* Name ist Anzeige, kein Muss */
      });
  }, [modell]);

  const loeschen = async () => {
    if (!modell) return;
    if (
      !(await bestaetige(`Modell${mitarbeiter ? ` von ${mitarbeiter}` : ''} ab ${formatIsoDate(modell.validFrom)} löschen?`, {
        ok: 'Löschen',
        gefaehrlich: true,
      }))
    )
      return;
    try {
      await deleteWorkSchedule(modell.id);
      navigate('/payroll/work-schedules');
    } catch {
      setError('Löschen fehlgeschlagen.');
    }
  };

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <Link
            to="/payroll/work-schedules"
            className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={14} /> Arbeitszeitmodelle
          </Link>
          <h1 className="text-3xl font-bold text-foreground">
            {mitarbeiter || 'Arbeitszeitmodell'}
            {modell && (
              <span className="ml-2 text-lg font-normal text-muted-foreground">
                ab {formatIsoDate(modell.validFrom)}
              </span>
            )}
          </h1>
        </div>
        {modell && !gesperrt && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setWechsel(true)}>
              <CalendarClock className="h-4 w-4" />
              <span className="hidden sm:inline">Wechsel</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => void loeschen()}>
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">Löschen</span>
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
      )}
      {!modell && !error && <p className="text-sm text-muted-foreground">Laden…</p>}
      {modell && <Maske key={modell.id} modell={modell} gesperrt={gesperrt} />}
      {modell && (
        <WorkScheduleChangeDialog
          isOpen={wechsel}
          onClose={() => setWechsel(false)}
          modell={modell}
          onAngelegt={(neu) => {
            setWechsel(false);
            navigate(`/payroll/work-schedules/${neu.id}`);
          }}
        />
      )}
    </div>
  );
}

function Maske({ modell, gesperrt }: { modell: WorkSchedule; gesperrt: boolean }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `workSchedule:${modell.id}`,
    bezeichnung: `Arbeitszeitmodell ab ${formatIsoDate(modell.validFrom)}`,
    daten: modell as unknown as Datensatz,
    schreibe: (patch, stand) =>
      api
        .patch<Datensatz>(`/payroll/work-schedules/${modell.id}`, patch, mitStand(stand))
        .then((r) => r.data),
    keepalive: { url: `/payroll/work-schedules/${modell.id}`, method: 'PATCH' },
  }) as Stelle;

  const gueltigAb = useFeld<string | null>(stelle, 'validFrom', {
    art: 'datum',
    pflicht: true,
    label: 'Gültig ab',
    gesperrt,
  });
  const beschaeftigung = useFeld(stelle, 'employment', { art: 'auswahl', label: 'Beschäftigung', gesperrt });
  // Teil 7: Die Wochenstunden rechnet der Server aus dem Tages-Soll bzw. dem Rahmen.
  const wochenstunden = useFeld<number | null>(stelle, 'weeklyHours', {
    art: 'zahl',
    stellen: 2,
    label: 'Wochenstunden',
    gesperrt: true,
  });
  const daten = useStellenDaten(stelle);
  const rahmen = (daten.hoursFrame as WorkScheduleFrame | null | undefined) ?? null;
  const art = useFeld<WorkScheduleFrame | null>(stelle, 'hoursFrame', {
    art: 'auswahl',
    label: 'Art',
    gesperrt,
    lies: (t) => (t === 'WEEK' || t === 'MONTH' ? t : null),
    schreib: (w) => w ?? '',
    // Folgefeld: Ein Rahmen braucht seine Stunden — vorgeschlagen aus den Wochenstunden.
    folge: (w) => {
      if (!w || daten.frameHours != null) return {};
      const woche = Number(daten.weeklyHours ?? 0);
      return { frameHours: w === 'MONTH' ? Math.round(((woche * 13) / 3) * 100) / 100 : woche };
    },
  });
  const rahmenStunden = useFeld<number | null>(stelle, 'frameHours', {
    art: 'zahl',
    stellen: 2,
    pflicht: true,
    label: rahmen === 'MONTH' ? 'Stunden je Monat' : 'Stunden je Woche',
    gesperrt,
    pruefe: (w) => (w !== null && (w <= 0 || w > 400) ? 'Mehr als 0, höchstens 400' : null),
  });
  const mehrzeit = useFeld<boolean>(stelle, 'frameOvertime', {
    art: 'auswahl',
    label: 'Über und unter dem Rahmen',
    gesperrt,
    lies: (t) => t !== 'false',
    schreib: (w) => (w === false ? 'false' : 'true'),
  });
  // AP-22 Teil 3: die Entlohnungsart ab diesem Gültig-ab; leer = Standard des Betriebs.
  const [arten, setArten] = useState<CollectiveAgreement[]>([]);
  useEffect(() => {
    fetchCollectiveAgreements()
      .then(setArten)
      .catch(() => setArten([]));
  }, []);
  const entlohnung = useFeld<string | null>(stelle, 'collectiveAgreementId', {
    art: 'auswahl',
    label: 'Entlohnungsart',
    gesperrt,
    lies: (t) => t || null,
    schreib: (w) => w ?? '',
  });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Abschnitt titel="Vereinbarung">
        <FeldZeile label="Gültig ab" status={gueltigAb}>
          <input type="date" className={feldKlasse} {...gueltigAb.props} />
        </FeldZeile>
        <FeldZeile label="Beschäftigung" status={beschaeftigung} hilfe="Entscheidet über den Zuschlagssatz für Mehrzeit.">
          <select className={feldKlasse} {...beschaeftigung.props}>
            <option value="FULL_TIME">Vollzeit</option>
            <option value="PART_TIME">Teilzeit</option>
          </select>
        </FeldZeile>
        <FeldZeile
          label={
            <span className="inline-flex items-center gap-1">
              Art
              <Hilfe>
                Tages-Soll: je Wochentag eine feste Stundenzahl (Normalfall). Stundenrahmen: Der Mitarbeiter ist
                für eine Stundenmenge je Woche oder Monat angestellt, wann er sie leistet, ist egal. Es gibt dann
                kein Tages-Soll; Urlaub, Krankenstand und Feiertag trägst du mit den Stunden von Hand ein.
              </Hilfe>
            </span>
          }
          status={art}
        >
          <select className={feldKlasse} {...art.props}>
            <option value="">Tages-Soll</option>
            <option value="WEEK">Stundenrahmen je Woche</option>
            <option value="MONTH">Stundenrahmen je Monat</option>
          </select>
        </FeldZeile>
        {rahmen && (
          <>
            <FeldZeile label={rahmen === 'MONTH' ? 'Stunden je Monat' : 'Stunden je Woche'} status={rahmenStunden}>
              <input inputMode="decimal" className={`${feldKlasse} text-right tabular-nums`} {...rahmenStunden.props} />
            </FeldZeile>
            <FeldZeile
              label="Über und unter dem Rahmen"
              status={mehrzeit}
              hilfe={
                mehrzeit.wert === false
                  ? 'Keine Mehrarbeit, keine Überstunden, kein Zeitausgleich — die Monatserfassung zeigt nur, ob der Rahmen erfüllt ist.'
                  : 'Über dem Rahmen entstehen Mehrarbeit und Überstunden wie gewohnt, darunter Minderstunden.'
              }
            >
              <select className={feldKlasse} {...mehrzeit.props}>
                <option value="true">Mehrzeit rechnen</option>
                <option value="false">Nur Kontrolle</option>
              </select>
            </FeldZeile>
          </>
        )}
        <FeldZeile
          label="Wochenstunden"
          status={wochenstunden}
          hilfe={
            rahmen === 'MONTH'
              ? 'Aus dem Monatsrahmen (Monat zu 13/3 Wochen) — bestimmt Teilzeit oder Vollzeit.'
              : rahmen
                ? 'Der Wochenrahmen.'
                : 'Summe des Tages-Solls; bei einem Zyklus der Schnitt der Wochen.'
          }
        >
          <input inputMode="decimal" className={`${feldKlasse} text-right tabular-nums`} {...wochenstunden.props} />
        </FeldZeile>
        {(arten.length > 0 || modell.collectiveAgreementId) && (
          <FeldZeile
            label="Entlohnungsart"
            status={entlohnung}
            hilfe="Kollektivvertrag oder pauschal. Ein Wechsel gegenüber dem vorigen Modell gilt nur ab einem Monatsersten."
          >
            <select className={feldKlasse} {...entlohnung.props}>
              <option value="">Standard des Betriebs</option>
              {arten
                .filter((a) => a.isActive || a.id === modell.collectiveAgreementId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          </FeldZeile>
        )}
      </Abschnitt>

      <Abschnitt titel="Zeitraum und Zyklus">
        <Feldgruppe
          stelle={stelle}
          label="Eintritt und Austritt"
          pruefe={(w) => {
            const von = w.entryDate as string | null;
            const bis = w.exitDate as string | null;
            return von && bis && bis < von ? 'Austritt liegt vor dem Eintritt' : null;
          }}
        >
          <Zeitraum stelle={stelle} gesperrt={gesperrt} />
        </Feldgruppe>
        <Feldgruppe
          stelle={stelle}
          label="Zyklus"
          pruefe={(w) => {
            const wochen = Number(w.cycleWeeks ?? 1);
            const anker = w.cycleAnchor as string | null;
            if (wochen >= 2 && !anker) return 'Ab zwei Zyklus-Wochen braucht es den Montag, an dem Woche 1 beginnt';
            if (anker && !istMontag(anker)) return 'Woche 1 muss an einem Montag beginnen';
            return null;
          }}
        >
          <Zyklus stelle={stelle} gesperrt={gesperrt} />
        </Feldgruppe>
      </Abschnitt>

      {!rahmen && (
        <div className="lg:col-span-2">
          <Abschnitt titel="Tages-Soll in Stunden">
            <SollRaster stelle={stelle} gesperrt={gesperrt} />
          </Abschnitt>
        </div>
      )}
    </div>
  );
}

function Zeitraum({ stelle, gesperrt }: { stelle: Stelle; gesperrt: boolean }) {
  const eintritt = useFeld<string | null>(stelle, 'entryDate', { art: 'datum', label: 'Eintritt', gesperrt });
  const austritt = useFeld<string | null>(stelle, 'exitDate', { art: 'datum', label: 'Austritt', gesperrt });
  return (
    <div className="grid grid-cols-2 gap-3">
      <FeldZeile label="Eintritt" status={eintritt}>
        <input type="date" className={feldKlasse} {...eintritt.props} />
      </FeldZeile>
      <FeldZeile label="Austritt" status={austritt}>
        <input type="date" className={feldKlasse} {...austritt.props} />
      </FeldZeile>
    </div>
  );
}

function Zyklus({ stelle, gesperrt }: { stelle: Stelle; gesperrt: boolean }) {
  const daten = useStellenDaten(stelle);
  const tage = (daten.days as WorkScheduleDay[] | undefined) ?? [];
  const wochen = useFeld<number | null>(stelle, 'cycleWeeks', {
    art: 'zahl',
    stellen: 0,
    pflicht: true,
    label: 'Zyklus-Wochen',
    gesperrt,
    pruefe: (w) => (w !== null && (w < 1 || w > 8 || !Number.isInteger(w)) ? 'Ganze Zahl von 1 bis 8' : null),
    // Folgefeld: Das Soll wächst oder schrumpft mit — im selben PATCH.
    folge: (w) => ({ days: sollFuer(tage, w ?? 1) }),
  });
  const anker = useFeld<string | null>(stelle, 'cycleAnchor', { art: 'datum', label: 'Woche 1 beginnt am', gesperrt });
  // Bei einem einwöchigen Modell ist jede Woche Woche 1 — der Anker wäre ein Feld ohne Wirkung.
  const mehrwoechig = Number(wochen.wert ?? 1) >= 2;
  return (
    <div className="grid grid-cols-2 gap-3">
      <FeldZeile
        label={
          <span className="inline-flex items-center gap-1">
            Zyklus-Wochen
            <Hilfe>
              Wie viele Wochen es dauert, bis sich die Arbeitszeit wiederholt. 1 = jede Woche gleich (Normalfall).
              2 = abwechselnd lange und kurze Woche, z. B. 42 h und 35 h.
            </Hilfe>
          </span>
        }
        status={wochen}
        hilfe="1 = jede Woche gleich"
      >
        <input inputMode="numeric" className={`${feldKlasse} text-right tabular-nums`} {...wochen.props} />
      </FeldZeile>
      {mehrwoechig && (
        <FeldZeile
          label={
            <span className="inline-flex items-center gap-1">
              Woche 1 beginnt am
              <Hilfe>
                Ein Montag, an dem Woche 1 des Zyklus gilt. Von dort zählt die Rechnung weiter: bei zwei Wochen ist
                die Woche danach Woche 2, die übernächste wieder Woche 1. Am einfachsten den Montag einer Woche
                wählen, von der du weißt, dass sie „lang“ war.
              </Hilfe>
            </span>
          }
          status={anker}
          hilfe="ein Montag"
        >
          <input type="date" className={feldKlasse} {...anker.props} />
        </FeldZeile>
      )}
    </div>
  );
}

/**
 * Das Tages-Soll als Liste (AP-36 8.3 a): Es geht als Ganzes, sobald der Fokus das
 * Raster verlässt — nicht je Zelle. Eine halb geänderte Woche wäre eine Woche, die
 * es so nie gab.
 */
function SollRaster({ stelle, gesperrt }: { stelle: Stelle; gesperrt: boolean }) {
  const daten = useStellenDaten(stelle);
  const gespeichert = useMemo(
    () =>
      sollFuer((daten.days as WorkScheduleDay[] | undefined) ?? [], Number(daten.cycleWeeks ?? 1)),
    [daten.days, daten.cycleWeeks],
  );
  const [eingabe, setEingabe] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>({ zustand: 'ruhig', fehler: null });

  // Neuer Stand vom Server (auch nach geänderter Zyklus-Länge): Eingaben verwerfen.
  useEffect(() => setEingabe({}), [gespeichert]);

  const zelle = (d: WorkScheduleDay) => `${d.week}-${d.weekday}`;
  const wertVon = (d: WorkScheduleDay) => eingabe[zelle(d)] ?? stunden(d.targetHours);

  const senden = useCallback(async (): Promise<void> => {
    if (Object.keys(eingabe).length === 0) return;
    const neu: WorkScheduleDay[] = [];
    for (const d of gespeichert) {
      const text = eingabe[zelle(d)];
      const h = text === undefined ? d.targetHours : text.trim() === '' ? 0 : leseZahl(text);
      if (h === null || h < 0 || h > 24) {
        // 3.3: Ungültig heißt nicht senden — Meldung am Raster, Eingabe bleibt stehen.
        setStatus({ zustand: 'fehler', fehler: 'Stunden je Tag zwischen 0 und 24' });
        return;
      }
      neu.push({ ...d, targetHours: h });
    }
    setStatus({ zustand: 'speichert', fehler: null });
    const ergebnis = await stelle.sende({
      kennung: 'days',
      felder: ['days'],
      labels: { days: 'Tages-Soll' },
      operation: () => ({ days: neu }),
    });
    if (ergebnis.art === 'fehler') {
      setStatus({ zustand: 'fehler', fehler: ergebnis.text, erneut: () => void senden() });
    } else {
      setStatus({ zustand: ergebnis.art === 'gespeichert' ? 'gespeichert' : 'ruhig', fehler: null });
      setTimeout(() => setStatus((z) => (z.zustand === 'gespeichert' ? { zustand: 'ruhig', fehler: null } : z)), 1500);
    }
  }, [eingabe, gespeichert, stelle]);

  // Speichern erst, wenn der Fokus das ganze Raster verlässt (Gruppe, 3.1).
  const beimVerlassen = (e: FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    void senden();
  };

  const wochen = Math.max(1, Number(daten.cycleWeeks ?? 1));
  const summe = (week: number) =>
    gespeichert
      .filter((d) => d.week === week)
      .reduce((s, d) => s + (leseZahl(wertVon(d)) ?? 0), 0);

  return (
    <div onBlur={beimVerlassen}>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium">Woche</th>
              {[1, 2, 3, 4, 5, 6, 7].map((wd) => (
                <th key={wd} className="px-2 py-1.5 text-center font-medium">
                  {weekdayName(wd).slice(0, 2)}
                </th>
              ))}
              <th className="px-3 py-1.5 text-right font-medium">Summe</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: wochen }, (_, i) => i + 1).map((week) => (
              <tr key={week} className="border-t border-border">
                <td className="px-3 py-1.5 text-muted-foreground">{week}</td>
                {gespeichert
                  .filter((d) => d.week === week)
                  .map((d) => (
                    <td key={d.weekday} className="px-1 py-1">
                      <input
                        inputMode="decimal"
                        disabled={gesperrt}
                        aria-label={`Woche ${week}, ${weekdayName(d.weekday)}`}
                        className={`${feldKlasse} text-center tabular-nums`}
                        value={wertVon(d)}
                        onChange={(e) => setEingabe((alt) => ({ ...alt, [zelle(d)]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setEingabe({});
                            setStatus({ zustand: 'ruhig', fehler: null });
                          }
                          if (e.key === 'Enter') void senden();
                        }}
                      />
                    </td>
                  ))}
                <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{stunden(summe(week))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          0 bedeutet: kein Arbeitstag. Gespeichert wird, sobald du das Raster verlässt; Esc stellt
          den gespeicherten Stand wieder her.
        </p>
        <FeldStatus status={status} />
      </div>
    </div>
  );
}
